import { useCallback, useMemo } from 'react';
import { useQuickAccess } from '../contexts/QuickAccessContext';

/**
 * useRecentPlugins - Thin compatibility wrapper over QuickAccessContext.
 * QuickAccessContext is the single canonical source for recency/frequency data.
 * This hook provides the same API surface expected by LayoutShell and mega menus.
 */

export interface RecentPluginsHook {
  recent: string[];
  trackUsage: (pluginId: string) => void;
  clearRecent: () => void;
  removeFromRecent: (pluginId: string) => void;
  isRecent: (pluginId: string) => boolean;
}

export function useRecentPlugins(maxRecent: number = 5): RecentPluginsHook {
  const { items, trackAccess, removeItem, clearAll } = useQuickAccess();

  const recent = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => b.accessedAt - a.accessedAt)
        .slice(0, maxRecent)
        .map(item => item.pluginId),
    [items, maxRecent],
  );

  const trackUsage = useCallback(
    (pluginId: string) => {
      if (!pluginId) return;
      const existing = items.find(i => i.pluginId === pluginId);
      trackAccess(pluginId, {
        title: existing?.title ?? pluginId,
        path: existing?.path ?? `/${pluginId}`,
        icon: existing?.icon,
        group: existing?.group,
      });
    },
    [trackAccess, items],
  );

  const removeFromRecent = useCallback(
    (pluginId: string) => removeItem(pluginId),
    [removeItem],
  );

  const isRecent = useCallback(
    (pluginId: string) => recent.includes(pluginId),
    [recent],
  );

  return {
    recent,
    trackUsage,
    clearRecent: clearAll,
    removeFromRecent,
    isRecent,
  };
}

export default useRecentPlugins;
